import { useRef, useState } from "react";
import { Play, Pause, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceSampleButton({ voices = [], voiceId, testid = "voice-sample-btn" }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const cache = useRef({});

  const voice = voices.find((v) => v.voice_id === voiceId);
  const isOpenAI = !!voiceId && voiceId.startsWith("openai:");
  const isNarrator = !!voiceId && voiceId.startsWith("narrator:");
  const canSample = !!voice?.preview_url || isOpenAI || isNarrator;

  const toggle = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    let src = voice?.preview_url;
    if (isOpenAI || isNarrator) {
      src = cache.current[voiceId];
      if (!src) {
        setLoading(true);
        try {
          const url = isNarrator ? `/voices/narrator-sample/${voiceId.split(":")[1]}` : `/voices/openai-sample/${voiceId.split(":")[1]}`;
          const { data } = await api.get(url);
          src = data.audio;
          cache.current[voiceId] = src;
        } catch (e) {
          toast.error(apiError(e));
          return;
        } finally {
          setLoading(false);
        }
      }
    }
    if (!src) return;
    const audio = new Audio(src);
    audio.onended = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    audioRef.current = audio;
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  if (!canSample) return null;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={loading}
      data-testid={testid}
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-sm border border-white/10 bg-[#0A0A0A] text-xs text-zinc-300 hover:bg-white/10 transition-colors disabled:opacity-60"
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      {playing ? "Stop" : "Sample"}
    </button>
  );
}
